import { Columns } from '#~/common/editor/index.js';
// Analytics domain wiring (merged from main): the media-controls
// discovery tracker + device-catalog context resolver feed the
// media-transition effect's connect/streaming success+error events.
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_NAMES, EVENT_CATEGORIES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import { createMediaControlsDiscoveryTracker } from '#~/analytics/domains/index.js';
import { useBrands, useTemplate } from '#~/device/registry/hooks.js';
import { StudioError } from '#~/error/index.js';
import { raiseError } from '#~/notifications/index.js';
import * as Badge from '@studio/design-system/badge';
import * as Dialog from '@studio/design-system/dialog';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import type { HtmlAttributes } from '@studio/design-system/props';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { isCausedBy } from '@studio/js-utils/logger';
import { Protocol } from '@studio/models/control';
import { Protocol as VideoProtocol } from '@studio/models/video';
import type { Capabilities } from '@studio/models/control/capabilities';
import type { Configuration as HttpConfig } from '@studio/models/control/http';
import type { Configuration as OnvifConfig } from '@studio/models/control/onvif';
import { toConfiguration } from '@studio/models/video';
import { useDelayedState, useLatestFn, useShallowEffect } from '@studio/react-utils/hooks';
import { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import deepmerge from 'deepmerge';
import { defaults } from 'lodash-es';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { shallow } from 'zustand/shallow';
import { Preview } from '../preview/index.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Panel } from '../store/types.js';
import { Credentials } from './credentials.js';
// Pure predicates for the credentials auth-verdict state machine.
// Per rule reference-rules-in-comments.
import {
  isAuthTrulySuccessful,
  isVerdictStaleForCredentials,
  resolveOnvifStreamVerdict,
  resolveLockoutHint,
  shouldBlockForAuth,
  shouldProceedOnSkippedAuth,
  shouldShowAuthError
} from './auth-verdict.js';
// Builds the NDI/RTSP video+control candidate from a known-model template.
// See utils.ts for the full rationale; W7 tests in discovery.test.ts exercise
// this helper directly.
//
// The pure helpers `computeKnownModelAutoConfigured` and
// `computeOnvifCapabilitiesAutoConfigured` are NOT imported here — they
// are consumed at the canonical recording site in
// `add/wiring.ts::handleCommit` (where the freshly-generated device id
// is authoritative), not inside these substitution effects (where the
// candidate carries no id). See discovery.test.ts for direct unit tests
// of those helpers.
import { buildKnownModelCandidate } from './utils.js';

/* @fixme - hack. */
type T_State = T_Optional<{
  authenticate: boolean;
  badge: Badge.Props;
  capabilities: Capabilities;
  credentials: OnvifConfig['credentials'];
  hasVideo: boolean;
  irisEnabled: boolean;
  authError: string | null;
  // True when the currently-selected discovery is a fingerprint-matched
  // known-model device whose HTTP control preview has resolved successfully
  // — discovery.tsx uses this to trigger the template-video substitution
  // effect below.
  isKnownModelHttpOk: boolean;
  // True at known-model INVALID sites to show the modal's Skip button (never at the legacy ONVIF site).
  authSkippable: boolean;
  // True while a known-model HTTP probe is running with no terminal verdict yet; arms the stall timeout.
  isKnownModelProbing: boolean;
  // True when the video preview tmp reported per-aspect Authentication VALIDATED (creds accepted).
  videoAuthValidated: boolean;
  // Media-transition analytics fields (merged from main): identify the
  // device + surface for the media connect/streaming events fired by the
  // media-transition effect. Populated by the state selector below.
  videoDeviceId: string | undefined;
  videoError: string | undefined;
  agentId: string | undefined;
  roomId: string | undefined;
  // ENG-4354: the video tmp's per-aspect Authentication === VALIDATING — the
  // RTSP driver's new in-flight signal (see flipAuthValidating in
  // bridge/encoder/pkg/driver/rtsp/rtsp.go). While true, a creds-bearing probe
  // is negotiating, so the modal must stay open and the "Invalid" error stays
  // suppressed (it is not a terminal verdict).
  videoAuthValidating: boolean;
  // True when the candidate's video plane carries credentials (video plane authenticates).
  videoAuthGating: boolean;
  // True when the verdict driving authError predates the submitted creds; consumed only by the error-display gate.
  authVerdictStale: boolean;
  // True only when authError comes from the video tmp's INVALID verdict (the RTSP plane, where
  // camera login lockouts surface); drives the lockout hint, never for the HTTP control plane.
  videoPlaneInvalid: boolean;
  // True when the ONVIF probe settled without a usable stream URI. A terminal,
  // non-success verdict: see OnvifStreamVerdict.
  onvifStreamMissing: boolean;
  // What the RTSP (ONVIF) stall budget counts from while the probe is pending: the
  // acknowledged control preview, else the add the Mediator sent for it.
  onvifProbeClock: string;
}>;

/** Max time a known-model probe may wait for a verdict before surfacing a connection-failed dialog. */
const STALL_TIMEOUT_MS = 15_000;

/**
 * Max time an RTSP (ONVIF) probe may go without capabilities or an error before it
 * counts as having no usable stream. Same budget as the known-model probe. It starts
 * when the Mediator sends the control preview add and restarts when the bridge
 * acknowledges it, so neither the wait for the Mediator nor a slow add is taken from
 * the camera. A fixed (non-PTZ) camera whose GetStreamUri fails publishes nothing,
 * and without a deadline reads "Connecting..." forever. An add the bridge does not
 * answer settles only when its state-sync request times out (30 s by default); the
 * budget settles it 15 s after it went out. Unlike the known-model stall this does
 * NOT clear the selection: the camera answered discovery, so Continue routes to the
 * RTSP helper seeded with its host instead.
 */
const ONVIF_STALL_TIMEOUT_MS = STALL_TIMEOUT_MS;

/**
 * RTSP (ONVIF) probe attempt ids. Module-wide so ids stay unique across Discovery
 * mounts, and a stall timer only ever matches the attempt it was started for.
 */
let onvifProbeAttempts = 0;

// Shared auth-block facts for every credentials-modal site: creds present => a prior
// submission was rejected ("Invalid credentials"); otherwise it's a first prompt
// ("Authentication Required"). Callers add their own authSkippable/authVerdictStale/hasVideo.
// Extracted per rule stay-in-scope (TS diff reduction).
const blockFacts = (creds: unknown): Pick<T_State, 'authenticate' | 'badge' | 'authError'> => ({
  authenticate: true,
  badge: { message: creds ? 'Invalid credentials' : 'Authentication Required' },
  authError: creds ? 'Invalid username or password. Please try again.' : null
});

/** `<Discovery>` component. */
export const Discovery = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const { track } = useAnalytics();
  const mediaControlsDiscoveryTracker = useMemo(
    () => createMediaControlsDiscoveryTracker(track),
    [track]
  );
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [forceAuthDialog, setForceAuthDialog] = useState(false);
  const mediaTransitionRef = useRef<{ hasVideo: boolean; error: string | undefined }>({
    hasVideo: false,
    error: undefined
  });

  // Device-registry template for the selected known-model discovery (has a modelId).
  const selectionModelId = useStore(({ source }) => source.selection?.modelId);
  const template = useTemplate(selectionModelId);

  // Brand-level fingerprint match (Change 2B): resolve a human-readable brand
  // name for the "brand matched" badge. Mirrors table.tsx's brandNameById
  // resolution — `useBrands()` is the registry collection, fall back to the
  // encoder's `brand` string (the wire's human-readable name) when the
  // registry lookup misses. Read here (a hook) and folded into the state
  // selector's brand-only branch below; the selector itself cannot call hooks.
  const brands = useBrands();
  const selectionBrandId = useStore(({ source }) => source.selection?.brandId);
  const selectionBrandFallback = useStore(({ source }) => source.selection?.brand);
  // Brand-only match ⇔ a brandId with NO modelId (a full model match carries
  // both). This is the row that can never preview-connect (empty video.path).
  const selectionHasVideo = useStore(({ source }) => !!source.selection?.video);
  const isBrandOnlyMatch = selectionHasVideo && !!selectionBrandId && !selectionModelId;
  const brandOnlyName =
    (selectionBrandId ? brands?.items?.[selectionBrandId]?.name : undefined) ??
    selectionBrandFallback;

  // Host from the selection, used to bind the template's templated video/
  // control configs (which don't carry a host — that comes from discovery).
  // `selection.video` is a union of video Configurations; most (RTSP/NDI/
  // Iris) carry `host`, a few (browsermedia) don't. The `'host' in` guard
  // narrows the type so we can read the field without a cast.
  const selectionHost = useStore(({ source }) => {
    const sel = source.selection;
    if (!sel || !('video' in sel) || !sel.video || !('host' in sel.video)) {
      return undefined;
    }
    return sel.video.host;
  });

  // What starts a new RTSP (ONVIF) probe attempt (see the probe attempts below): selecting
  // the row, and submitting credentials, which writes them onto the candidate's control.
  const isOnvifRow = useStore(
    ({ source }) => source.selection?.control?.protocol === Protocol.ONVIF
  );
  const selectionId = useStore(({ source }) => source.selection?.id);
  const controlCredentials = useStore(({ candidate }) =>
    candidate.control && 'credentials' in candidate.control
      ? candidate.control.credentials
      : undefined
  );

  /*
   * This is all a workaround for not having declarative device records.
   *
   * @fixme - Agents need to support declarative devices.
   */

  // Prior to having an actual device record, we'll facilitate feedback messages.
  const state = useStore(
    ({
      candidate,
      external,
      previews,
      pendingPreviews,
      source,
      agentId,
      roomId,
      authSkipped
    }): T_State => {
      const { selection } = source;

      // Has the user actually selected a discovery device?
      if (!selection) {
        return {
          // Nothing to do yet.
        };
      }

      // Per-aspect auth verdict VALIDATED from the video tmp (RTSP/RTSPLT/NDI): creds accepted.
      const videoAuthValidated =
        !!previews.video?.deviceId &&
        external.transients[previews.video.deviceId]?.videoAuthenticationStatus ===
          AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED;

      // Per-aspect auth verdict VALIDATING from the video tmp: a creds-bearing probe is in-flight.
      const videoAuthValidating =
        !!previews.video?.deviceId &&
        external.transients[previews.video.deviceId]?.videoAuthenticationStatus ===
          AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATING;

      // True when the candidate's video plane carries credentials (video plane authenticates).
      const videoAuthGating = !!(
        candidate.video &&
        'credentials' in candidate.video &&
        candidate.video.credentials
      );

      // Keep track of what we know to be true.
      let facts: T_State = {
        videoAuthValidated,
        videoAuthValidating,
        videoAuthGating,
        // Default fresh; control/video INVALID sites below set this when the read tmp predates the creds.
        authVerdictStale: false,
        // Do we have an actual video preview device?
        hasVideo: !!previews.video?.deviceId,
        videoDeviceId: previews.video?.deviceId,
        videoError:
          previews.video?.error instanceof Error
            ? previews.video.error.message
            : previews.video?.error
              ? String(previews.video.error)
              : undefined,
        agentId,
        roomId: roomId?.toString()
      };

      // Are with dealing with ONVIF discovery?
      if (
        [selection.control?.protocol, candidate.control?.protocol].every(
          (µ) => µ === Protocol.ONVIF
        )
      ) {
        // The control preview record exists once the mediator's add returned, accepted
        // or rejected. Until then the probe reads like one that has published nothing,
        // and the stall budget below covers both once the add has gone out.
        const deviceId = previews.control?.deviceId;
        const error = previews.control?.error;
        const { credentials } = candidate.control as OnvifConfig;

        // Do we have an error?
        if (error) {
          // Check if this is an authentication error.
          //
          // ENG-4220 audit note: unlike the dead 'NatsError: TIMEOUT' discovery
          // guard, these case-sensitive substrings ARE live — the encoder's SOAP
          // layer (bridge/encoder/pkg/protocol/soap/conn.go) formats errors as
          // "unexpected response status code: %d: %s" with http.StatusText, which
          // yields exactly "400: Bad Request" / "401: Unauthorized" /
          // "403: Forbidden". Keep in sync with that format if it ever changes.
          const isAuthError =
            isCausedBy(error, '400: Bad Request') ||
            isCausedBy(error, '401: Unauthorized') ||
            isCausedBy(error, '403: Forbidden');

          // Whoops.  We have an error.  What went wrong?
          return deepmerge(
            {
              ...facts,
              // A non-credential failure is terminal for this probe: no stream URI will
              // come, so footer.tsx routes Continue to the RTSP helper rather than
              // leaving it on "Waiting for preview..." next to this error badge.
              // footer.tsx reads the same error first (resolveOnvifStreamVerdict).
              onvifStreamMissing: !isAuthError,
              badge: {
                message: 'ONVIF connection failed',
                severity: 'error' as const
              }
            },
            // Did we fail authentication?
            isAuthError ? blockFacts(credentials) : {}
          );
        }

        /*
         * For ONVIF, the whole point of the endeavor is to wait for the transient ONVIF control
         * device to show up in the preview, and query its capabilities for the RTSP video URI.
         */

        // Has the transient ONVIF device shown up?
        const capabilities = deviceId
          ? external.transients[deviceId]?.control?.capabilities
          : undefined;

        // One verdict for the badge, the credentials modal and the footer's Continue
        // gate (footer.tsx reads the same inputs). `stalled` belongs to the current
        // probe attempt only (see the stall timer below).
        const verdict = resolveOnvifStreamVerdict(capabilities, {
          stalled: !!source.onvifProbe?.stalled
        });

        if (verdict === 'pending') {
          // Still waiting for the transient ONVIF device. The stall budget keeps this
          // from lasting forever; it counts from the acknowledged preview, else from the
          // add the Mediator sent, and does not run before the add goes out.
          const sentId = pendingPreviews.control?.deviceId;
          return {
            ...facts,
            ...(deviceId
              ? { onvifProbeClock: `acknowledged:${deviceId}` }
              : sentId
                ? { onvifProbeClock: `sent:${sentId}` }
                : {}),
            badge: {
              message: 'Connecting...'
            }
          };
        }

        facts = { ...facts, capabilities, credentials };

        // The camera returned no usable stream URI: a PTZ camera whose GetStreamUri
        // failed still reports movement, a fixed camera reports nothing until the stall
        // timer fires, and a URI toConfiguration cannot map (an http:// one) builds no
        // video. Stop here with an explicit warning instead of an endless
        // "Connecting..."; footer.tsx routes Continue to the RTSP helper seeded with
        // this camera's host.
        if (verdict === 'missing') {
          return {
            ...facts,
            onvifStreamMissing: true,
            badge: { message: 'No usable stream URL', severity: 'warning' as const }
          };
        }
      }

      // Known-model HTTP credential-probe branch: watches previews.control.error
      // and controlAuthenticationStatus for the auth verdict. INVALID opens the
      // credentials dialog; VALIDATED flips isKnownModelHttpOk for substitution.
      if (
        candidate.modelId &&
        !selection.irisEnabled &&
        candidate.control?.protocol === Protocol.HTTP &&
        previews.control
      ) {
        const { deviceId, error } = previews.control;
        const { credentials } = candidate.control as HttpConfig;
        // HTTP is a control-plane driver, so its verdict lives in controlAuthenticationStatus.
        const tmpAuthStatus = external.transients[deviceId]?.controlAuthenticationStatus;

        const hasErrorAuthFail =
          error &&
          (isCausedBy(error, '400: Bad Request') ||
            isCausedBy(error, '401: Unauthorized') ||
            isCausedBy(error, '403: Forbidden'));

        const hasStatusAuthFail =
          tmpAuthStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID;

        // Block for auth unless the operator skipped (then proceed to substitution below).
        const blockForAuth = shouldBlockForAuth({
          statusInvalid: hasStatusAuthFail,
          errorAuthFail: !!hasErrorAuthFail,
          authSkipped
        });

        if (blockForAuth) {
          return {
            ...facts,
            ...blockFacts(credentials),
            authSkippable: true,
            // Mark the verdict stale if this control tmp predates the submitted creds (suppresses the error flash).
            authVerdictStale: isVerdictStaleForCredentials(previews.control?.urx, !!credentials)
          };
        }

        // Non-auth error (network/DNS): show a connection-failed badge, no creds modal.
        if (error && !hasErrorAuthFail) {
          return {
            ...facts,
            badge: { message: 'Connection failed', severity: 'error' as const }
          };
        }

        // Terminal non-blocking verdicts (VALIDATED/UNSUPPORTED/ERROR) proceed to substitution.
        // Timing hazard: do NOT flip isKnownModelHttpOk while still waiting (UNKNOWN/VALIDATING/
        // undefined) or the HTTP probe tmp is torn down before an INVALID verdict can arrive.
        if (
          tmpAuthStatus === AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED ||
          tmpAuthStatus === AuthenticationStatus.AUTHENTICATION_STATUS_UNSUPPORTED ||
          tmpAuthStatus === AuthenticationStatus.AUTHENTICATION_STATUS_ERROR ||
          // Skip override: operator skipped an auth-failed control probe, so proceed like a terminal verdict.
          shouldProceedOnSkippedAuth({
            statusInvalid: hasStatusAuthFail,
            errorAuthFail: !!hasErrorAuthFail,
            authSkipped
          })
        ) {
          facts = { ...facts, isKnownModelHttpOk: true, credentials };
        } else {
          // Still waiting for a verdict; flag it so the stall-timeout effect arms a timer.
          facts = { ...facts, isKnownModelProbing: true };
        }
      }

      // Source-agnostic catch: open the credentials modal whenever a known-model
      // video probe publishes INVALID, regardless of the original discovery source.
      if (candidate.modelId && previews.video?.deviceId) {
        // The RTSP/RTSPLT/NDI driver writes its verdict into videoAuthenticationStatus.
        const videoTmpAuthStatus =
          external.transients[previews.video.deviceId]?.videoAuthenticationStatus;
        // Block for auth on a video-plane INVALID unless the operator skipped.
        if (
          shouldBlockForAuth({
            statusInvalid:
              videoTmpAuthStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID,
            errorAuthFail: false,
            authSkipped
          })
        ) {
          // Only RTSP-family video configs carry `credentials`; guard the narrowing.
          const videoCredentials =
            candidate.video && 'credentials' in candidate.video
              ? candidate.video.credentials
              : undefined;
          return {
            ...facts,
            // Force hasVideo: false so the render routes to the modal, not a broken <Preview/>.
            hasVideo: false,
            ...blockFacts(videoCredentials),
            authSkippable: true,
            // Mark the verdict stale if this video tmp predates the submitted creds (suppresses the error flash).
            authVerdictStale: isVerdictStaleForCredentials(previews.video?.urx, !!videoCredentials),
            // This INVALID is the video plane's own verdict: eligible for the lockout hint.
            videoPlaneInvalid: true
          };
        }
      }

      // RTSP/NDI known-model (fingerprint-matched) video discovery branch. Both protocols
      // share the error/hasVideo/connecting skeleton and differ only in the badge label and
      // which plane carries credentials: RTSP creds live on the video plane; NDI video configs
      // carry none, so its creds live on the HTTP control probe. INVALID is handled by the
      // source-agnostic check above; this keeps only the synchronous-error path.
      if (
        'video' in selection &&
        selection.modelId &&
        (selection.video?.protocol === VideoProtocol.RTSP ||
          selection.video?.protocol === VideoProtocol.NDI)
      ) {
        const isNdi = selection.video?.protocol === VideoProtocol.NDI;
        const label = isNdi ? 'NDI' : 'RTSP';
        const authCredentials = isNdi
          ? candidate.control && 'credentials' in candidate.control
            ? candidate.control.credentials
            : undefined
          : candidate.video && 'credentials' in candidate.video
            ? candidate.video.credentials
            : undefined;

        if (previews.video?.error) {
          const error = previews.video.error;
          const isAuthError =
            isCausedBy(error, '400: Bad Request') ||
            isCausedBy(error, '401: Unauthorized') ||
            isCausedBy(error, '403: Forbidden');

          // Block for auth on a synchronous auth error unless the operator skipped.
          const blockForAuth = shouldBlockForAuth({
            statusInvalid: false,
            errorAuthFail: isAuthError,
            authSkipped
          });

          return deepmerge(
            {
              ...facts,
              badge: {
                message: `${label} connection failed`,
                severity: 'error' as const
              }
            },
            blockForAuth ? { ...blockFacts(authCredentials), authSkippable: true } : {}
          );
        }

        if (facts.hasVideo) {
          return {
            ...facts,
            badge: { message: 'Device identified', severity: 'success' as const }
          };
        }

        return {
          ...facts,
          badge: { message: 'Connecting...' }
        };
      }

      // Are we dealing with an iris enabled device?
      if (selection.irisEnabled) {
        return {
          ...facts,
          irisEnabled: true,
          badge: { message: `${vendorName()} enabled device`, severity: 'success' as const }
        };
      }

      // Brand-level fingerprint match (Change 2B): a brandId with NO modelId.
      // Its video.path is empty, so a preview can never connect — so, exactly
      // like the Iris-enabled exception above, we attempt NO connection and
      // surface a small "brand matched" success badge instead of the generic
      // "Connecting…" spinner. footer.tsx::handleNext routes Continue straight
      // to the RTSP Helper (Panel.Video) with the brand pre-seeded.
      if (isBrandOnlyMatch) {
        return {
          ...facts,
          badge: {
            message: brandOnlyName ? `${brandOnlyName} matched` : 'Brand matched',
            severity: 'success' as const
          }
        };
      }

      /*
       * Now that we've let the ONVIF discovery do its thing, we can focus on the video preview.
       * Was there a video connection error?
       */
      if (previews.video?.error) {
        // Video connection failed.
        return {
          ...facts,
          badge: {
            message: 'Video connection failed',
            severity: 'error' as const
          },
          videoError:
            previews.video.error instanceof Error
              ? previews.video.error.message
              : String(previews.video.error)
        };
      }

      // If we made it this far, we must be waiting for the video preview to show up.
      return {
        ...facts,
        badge: {
          message: 'Connecting...'
        }
      };
    },
    shallow
  );
  const deviceAnalyticsCatalogContext = useStore(({ candidate, manufacturer }) => {
    const selectedModel = manufacturer?.config?.model;

    return resolveDeviceAnalyticsCatalogContextFromModel(selectedModel, candidate.modelId);
  }, shallow);

  // Skip-credentials handler: closes the modal and sets the persistent authSkipped flag so the add proceeds.
  const skipInFlightRef = useRef(false);
  const handleSkip = useLatestFn(() => {
    // Track the Skip action.
    // Per rule: stay-in-scope (ai.knowledge/workflow) — a NEW event, nothing renamed.
    track(EVENT_NAMES.DEVICE_CREDENTIALS_SKIPPED, EVENT_CATEGORIES.DEVICE, {
      add_mode: 'one_click',
      ...(state.roomId ? { studio_id: state.roomId } : {}),
      ...deviceAnalyticsCatalogContext,
      ...(state.videoDeviceId ? { deviceId: state.videoDeviceId } : {})
    });
    skipInFlightRef.current = true; // guard onOpenChange
    producer((draft) => {
      draft.authSkipped = true;
    });
    setForceAuthDialog(false); // optimistic close
    setIsAuthenticating(false);
    setCredentialsError(null);
  });

  // Clear the skip guard once the dialog is fully closed (Radix does not fire onOpenChange
  // on a programmatic close, so the ref would otherwise swallow the next user-initiated close).
  useEffect(() => {
    if (!state.authenticate && !forceAuthDialog) {
      skipInFlightRef.current = false;
    }
  }, [state.authenticate, forceAuthDialog]);

  useEffect(
    useLatestFn(() => {
      const { capabilities, credentials } = state;

      // Look in the capabilities and get the video URI (if applicable)
      const { uri } = capabilities?.discovery ?? {};

      producer(({ candidate, panel, previewPaused }) => {
        // Source step only: Scaffold keeps this panel mounted through its exit fade, and by
        // then Configure video owns candidate.video. Read at write time, not at render.
        // Save owns a snapshot while it drains previews; their removal must not erase its stream.
        if (panel !== Panel.Source || previewPaused) return;

        // Convert the discovery URL to a video configuration or `undefined`.
        candidate.video = toConfiguration(defaults({}, uri, credentials));

        // Auto-configured field recording happens at the canonical
        // commit-time seam in `add/wiring.ts::handleCommit`, where the
        // freshly-generated device id is authoritative. The substitution
        // effect runs before the id exists, so any dispatch from here
        // would be a no-op. See `computeOnvifCapabilitiesAutoConfigured`
        // in `source/utils.ts` (consumed by handleCommit).
      });
    }),
    [state.capabilities?.discovery?.uri]
  );

  // Known-model HTTP substitution: once the credential probe connects, swap in the
  // template's video/control configs. Guarded on candidate.video unset to run once per cycle.
  useEffect(
    useLatestFn(() => {
      if (!state.isKnownModelHttpOk || state.authenticate) return;
      if (!selectionHost) return;

      const { credentials } = state;

      producer(({ candidate, source, previewPaused }) => {
        if (previewPaused || candidate.video) return;

        // Build the NDI/RTSP video+control substitution from the template.
        const substitution = buildKnownModelCandidate(
          source.selection,
          template,
          credentials,
          selectionHost
        );
        if (!substitution) return;

        candidate.video = substitution.video;
        if (substitution.control !== undefined) {
          candidate.control = substitution.control;
        }

        // Auto-configured field recording happens at the canonical
        // commit-time seam in `add/wiring.ts::handleCommit` via
        // `computeKnownModelAutoConfigured` (consumed there, not here).
        // The substitution effect runs before the device id exists, so
        // any dispatch from this seam would be a no-op.
      });
    }),
    [state.isKnownModelHttpOk, state.authenticate, state.credentials, template, selectionHost]
  );

  // Stall-timeout safety net: if the known-model probe stays unresolved for STALL_TIMEOUT_MS,
  // raise a connection-failed toast and clear the selection so the user gets an escape hatch.
  useEffect(() => {
    if (!state.isKnownModelProbing) return;
    const timer = window.setTimeout(() => {
      raiseError(
        new StudioError(
          'The device did not respond in time. Check that it is reachable and try again.'
        ),
        { title: 'Connection failed', autoClose: 8_000 }
      );
      producer((draft) => {
        draft.source.selection = undefined;
        draft.source.preferredId = undefined;
      });
    }, STALL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isKnownModelProbing]);

  // RTSP (ONVIF) probe attempts. Selecting the row or submitting credentials starts a new
  // one, so a stall from the previous row or login never carries over. The ref hands the
  // attempt to the stall timer below, which runs after this effect in the same commit.
  const onvifAttemptRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!isOnvifRow) return;

    onvifProbeAttempts += 1;
    const attempt = onvifProbeAttempts;
    onvifAttemptRef.current = attempt;
    producer((draft) => {
      // Source step only (see the uri effect above).
      if (draft.panel !== Panel.Source) return;
      draft.source.onvifProbe = { attempt, stalled: false };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnvifRow, selectionId, controlCredentials]);

  // RTSP (ONVIF) stall timer for the current attempt. It runs from the moment the
  // Mediator sends the probe's control preview add and restarts when the bridge
  // acknowledges it (state.onvifProbeClock); before the add goes out there is no timer.
  // When it fires the attempt is marked stalled, and resolveOnvifStreamVerdict reads
  // 'missing' unless an error or capabilities arrived first: the badge reports no
  // usable stream and footer.tsx routes Continue to the RTSP helper.
  useEffect(() => {
    const clock = state.onvifProbeClock;
    const attempt = onvifAttemptRef.current;
    if (!clock || attempt === undefined) return;

    const timer = window.setTimeout(() => {
      producer((draft) => {
        // Only the attempt still running; a newer selection or login is not stalled.
        if (draft.panel !== Panel.Source || draft.source.onvifProbe?.attempt !== attempt) return;
        draft.source.onvifProbe = { attempt, stalled: true };
      });
    }, ONVIF_STALL_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.onvifProbeClock, isOnvifRow, selectionId, controlCredentials]);

  // Media-transition discovery analytics (merged from main): on the
  // rising edge of `hasVideo` (or a newly-arrived `videoError`) fire the
  // media connect/streaming success+error events through the domain
  // `mediaControlsDiscoveryTracker`. Kept as its OWN effect — orthogonal
  // to HEAD's auth-success effect below — so the two dependency arrays
  // stay independent and neither re-fires on the other's state changes.
  useEffect(() => {
    const previous = mediaTransitionRef.current;
    const commonProperties = {
      ...(state.agentId ? { agent_id: state.agentId } : {}),
      ...(state.roomId ? { studio_id: state.roomId } : {}),
      linking_surface: 'add_device'
    };

    if (state.hasVideo && !previous.hasVideo) {
      mediaControlsDiscoveryTracker.trackMediaConnectSuccess({
        ...commonProperties,
        ...deviceAnalyticsCatalogContext,
        ...(state.videoDeviceId ? { device_id: state.videoDeviceId } : {}),
        media_type: 'video',
        surface: 'add_device'
      });
      mediaControlsDiscoveryTracker.trackMediaStreamingSuccess(
        {
          ...commonProperties,
          ...deviceAnalyticsCatalogContext,
          ...(state.videoDeviceId ? { device_id: state.videoDeviceId } : {}),
          media_type: 'video',
          surface: 'add_device'
        },
        // ENG-4313 (AI-review B1 fix): the add_device/preview surface must NOT mint the
        // canonical media_stream_succeeded. `state.videoDeviceId` here is the TRANSIENT
        // preview id (getTransientId → isTransient() === true); on commit the device is torn
        // down and the room observer mints a FRESH, non-transient id (wiring.ts generateId).
        // Transient id ≠ committed id, so a cross-surface dedupe key built from it could
        // never collide with the observer's key — the earlier attempt double-counted every
        // camera add and polluted the funnel with `transient:` device_ids. A never-committed
        // preview is not a real room "first stream" either. So the committed-id room observer
        // (device-lifecycle-analytics-observer) is the single first-stream source; the
        // transient preview emits no stream-success row.
        { emitCanonical: false }
      );
    }

    if (state.videoError && state.videoError !== previous.error) {
      mediaControlsDiscoveryTracker.trackMediaConnectError({
        ...commonProperties,
        ...deviceAnalyticsCatalogContext,
        ...(state.videoDeviceId ? { device_id: state.videoDeviceId } : {}),
        media_type: 'video',
        surface: 'add_device',
        error_message: state.videoError
      });
      mediaControlsDiscoveryTracker.trackMediaStreamingError({
        ...commonProperties,
        ...deviceAnalyticsCatalogContext,
        ...(state.videoDeviceId ? { device_id: state.videoDeviceId } : {}),
        media_type: 'video',
        surface: 'add_device',
        error_message: state.videoError
      });
    }

    mediaTransitionRef.current = {
      hasVideo: !!state.hasVideo,
      error: state.videoError
    };
  }, [
    state.hasVideo,
    state.videoError,
    state.videoDeviceId,
    state.agentId,
    state.roomId,
    deviceAnalyticsCatalogContext,
    mediaControlsDiscoveryTracker
  ]);

  useEffect(() => {
    // Auth succeeds on any of: ONVIF capabilities carrying a stream URI (legacy), a non-INVALID
    // known-model HTTP verdict, or the video tmp reporting VALIDATED. The control arm is gated on
    // !videoAuthGating so a video-drives-auth camera doesn't close the modal before
    // videoAuthValidated arrives. Movement-only ONVIF capabilities are NOT success: they carry no
    // stream for Configure video (see OnvifStreamVerdict).
    const authSuccess = isAuthTrulySuccessful({
      legacyCapabilities: resolveOnvifStreamVerdict(state.capabilities) === 'ready',
      videoAuthValidated: !!state.videoAuthValidated,
      controlValidated: !!state.isKnownModelHttpOk && !state.authenticate,
      videoAuthGating: !!state.videoAuthGating
    });

    if (authSuccess && isAuthenticating) {
      // Authentication successful
      setIsAuthenticating(false);
      setForceAuthDialog(false);
      setCredentialsError(null);
    } else if (state.onvifStreamMissing && isAuthenticating) {
      // Terminal NON-success: the probe settled without a usable stream URI (see
      // OnvifStreamVerdict). Re-typing the password cannot fix that, and leaving
      // isAuthenticating pinned would hold the modal open on a spinner forever. Close it;
      // the badge says why, and Continue routes to the RTSP helper (footer.tsx
      // shouldRouteOnvifToRtspHelper).
      setIsAuthenticating(false);
      setForceAuthDialog(false);
      setCredentialsError(null);
    } else if (!state.authenticate && !isAuthenticating) {
      // Dialog closing naturally
      setForceAuthDialog(false);
      setCredentialsError(null);
    } else if (isAuthenticating) {
      // Keep dialog open during authentication (isAuthenticating pins it through the resync window).
      setForceAuthDialog(true);
    }

    // Error-display gate: show the "Invalid" error only on a fresh terminal INVALID,
    // never while a probe is VALIDATING or the read verdict is stale (avoids the error flash).
    setCredentialsError(
      shouldShowAuthError({
        hasAuthError: !!state.authError,
        isAuthenticating,
        videoAuthValidating: !!state.videoAuthValidating,
        verdictStale: !!state.authVerdictStale
      })
        ? (state.authError ?? null)
        : null
    );
  }, [
    state.authenticate,
    state.authError,
    state.capabilities,
    state.isKnownModelHttpOk,
    state.videoAuthValidated,
    state.videoAuthValidating,
    state.videoAuthGating,
    state.authVerdictStale,
    state.onvifStreamMissing,
    isAuthenticating
  ]);

  // Introduce a delay before we render the badge.
  const [badge, setBadge] = useDelayedState<T_Maybe<Badge.Props>>(undefined);

  useShallowEffect(() => {
    const nextBadge = state.badge;

    setBadge(undefined, 0);

    if (nextBadge) {
      setBadge(nextBadge, 1e3);
    }
  }, [state.badge]);

  return (
    <Columns.Column {...props}>
      {state.hasVideo ? (
        <Preview />
      ) : (
        <>
          <Preview>
            <Badge.Container>{!!badge && <Badge.Root {...badge} />}</Badge.Container>
          </Preview>

          <AnimatePresence>
            {(!!state.authenticate || forceAuthDialog) && (
              <motion.div {...Motions.Fade}>
                <Dialog.Dialog
                  open={!!state.authenticate || forceAuthDialog}
                  onOpenChange={(open) => {
                    if (!open) {
                      // Skip guard: don't clear the selection on the Skip button's programmatic close.
                      if (skipInFlightRef.current) {
                        skipInFlightRef.current = false;
                        return;
                      }

                      // Clear the forced dialog state
                      setForceAuthDialog(false);
                      setIsAuthenticating(false);

                      // Only clear the source selection if we're not in authentication process
                      producer((draft) => {
                        // De-select the current source, in turn effectively clearing up the UI
                        draft.source.selection = undefined;
                        draft.source.preferredId = undefined;
                      });

                      // Clear error when dialog is closed
                      setCredentialsError(null);
                    }
                  }}
                >
                  <Dialog.Overlay className='fixed inset-0'>
                    <Dialog.Content className='fixed left-1/2 top-1/2 flex min-h-[400px] w-[506px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-12 bg-base-200 shadow-lg'>
                      <Dialog.Header title='Device Credentials' className='mb-20' closeable />
                      <div className='flex flex-grow flex-col gap-20 px-40 py-20'>
                        <Credentials
                          error={credentialsError}
                          onErrorChange={setCredentialsError}
                          onAuthenticationStart={() => setIsAuthenticating(true)}
                          // Skip button visible only for the known-model INVALID verdict.
                          canSkip={!!state.authSkippable}
                          onSkip={handleSkip}
                          // Lockout hint under a video-plane "Invalid" error (see AUTH_LOCKOUT_HINT).
                          notice={resolveLockoutHint({
                            errorShown: !!credentialsError,
                            videoPlaneInvalid: !!state.videoPlaneInvalid
                          })}
                        />
                      </div>
                    </Dialog.Content>
                  </Dialog.Overlay>
                </Dialog.Dialog>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </Columns.Column>
  );
};
